import { useEffect, useRef, useState } from 'react';
import { API } from '../service/ProductsService';

const formatPrice = (n) => `₪${Number(n).toFixed(2)}`;

const s = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 150 },
  dialog:   { width: '100%', maxWidth: 400, background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,.2)' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 },
  title:    { margin: 0, fontSize: 20, fontWeight: 700 },
  close:    { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#59636e' },
  product:  { fontWeight: 600, marginBottom: 2 },
  store:    { color: '#59636e', fontSize: 14, marginBottom: 14 },
  current:  { display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#59636e', padding: '8px 0', borderTop: '1px solid #eee' },
  label:    { display: 'block', fontSize: 13, color: '#59636e', margin: '12px 0 4px' },
  inputRow: { display: 'flex', alignItems: 'center', gap: 8 },
  shekel:   { fontSize: 20, fontWeight: 700 },
  input:    { flex: 1, padding: '10px 12px', fontSize: 20, fontWeight: 700, border: '1px solid #d0d7de', borderRadius: 10, direction: 'ltr', textAlign: 'right' },
  hint:     { fontSize: 12, color: '#59636e', margin: '8px 0 14px', lineHeight: 1.5 },
  error:    { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 14 },
  submit:   { width: '100%', padding: '11px', fontSize: 16, fontWeight: 700, border: 'none', borderRadius: 10, background: '#1a3f9c', color: '#fff' },
};

// עדכון מחיר של מוצר בחנות אחת (spReportPrice). נפתח מ-ProductRow, רק למשתמש מחובר.
// price - השורה מהמחירים (productId, price, sitePrice...); onDone(result) - עם התשובה מהשרת
const PriceReportDialog = ({ item, store, price, onDone, onClose }) => {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    const num = Number(String(value).replace(',', '.'));
    if (!(num > 0)) { setError('נא להזין מחיר תקין'); return; }   // ולידציה גם ב-SP
    setSending(true);
    setError(null);
    try {
      const resp = await API.reportPrice(price.productId, Math.round(num * 100) / 100);
      const status = resp?.[0]?.[0];
      if (status?.code === 0 && resp?.[1]?.[0]) onDone(resp[1][0]);
      else if (status?.code !== 90) setError(status?.msg || 'שגיאה בעדכון המחיר');   // 90 - חלון הכניסה נפתח לבד
    } catch (err) {
      setError(err.message || 'שגיאה בעדכון המחיר');
    } finally {
      setSending(false);
    }
  };

  const sitePrice = price.sitePrice ?? price.price;

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form style={s.dialog} role="dialog" aria-modal="true" aria-labelledby="report-title" onSubmit={submit} noValidate>
        <div style={s.header}>
          <h2 id="report-title" style={s.title}>עדכון מחיר</h2>
          <button type="button" style={s.close} onClick={onClose} aria-label="סגירה">×</button>
        </div>

        <div style={s.product}>{item.name}</div>
        <div style={s.store}>ב{store.name}</div>

        <div style={s.current}><span>מחיר באתר החנות</span><span className="num">{formatPrice(sitePrice)}</span></div>
        {price.reportedPrice != null && (
          <div style={s.current}><span>עודכן לאחרונה ע"י משתמש</span><span className="num">{formatPrice(price.reportedPrice)}</span></div>
        )}

        <label style={s.label} htmlFor="report-price">המחיר בפועל בחנות{item.isWeighable ? ' (לק"ג)' : ''}</label>
        <div style={s.inputRow}>
          <span style={s.shekel}>₪</span>
          <input
            id="report-price"
            ref={inputRef}
            style={s.input}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^\d.,]/g, ''))}
            placeholder={Number(sitePrice).toFixed(2)}
            maxLength={9}
          />
        </div>
        <p style={s.hint}>המחיר יוצג לכל המשתמשים כ"עודכן ע"י משתמש", לצד המחיר מהאתר, וישמש להשוואה עד שהמחיר באתר החנות ישתנה.</p>

        {error && <div style={s.error} role="alert">{error}</div>}

        <button type="submit" style={s.submit} disabled={sending || !value}>{sending ? 'שולח…' : 'עדכון המחיר'}</button>
      </form>
    </div>
  );
};

export default PriceReportDialog;
